/**
 * First Glance: spot-gamma peak from current options table (same pipeline as Hybrid / Spot Gamma chart).
 * Peak = strike with largest |spotGamma|; GEX sign at that strike drives magnetic vs repulsive read
 * (same classifyGexStructure as Hybrid / scannerDataToGammaStrikeData).
 *
 * When `expiryIso` (YYYY-MM-DD) is set, only that expiration's rows are used (matches First Glance
 * expiry filter). Else when `targetDte` is set, rows are restricted to the nearest calendar DTE slice.
 */

import type { DataMode } from '../types/DataMode';
import type { ScannerData } from '../types/ScannerData';
import { scannerDataToGammaStrikeData, classifyGexStructure } from './gammaChartUtils';

function parseExpiryDay(exp: string): Date | null {
  const s = String(exp || '').trim();
  if (!s) return null;
  const d = new Date(s.includes('T') ? s : `${s}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Local calendar YYYY-MM-DD for matching scanner rows to First Glance expiry options. */
export function normalizeScannerExpiryToIso(raw: string): string | null {
  const d = parseExpiryDay(raw);
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function calendarDteForExpiry(expRaw: string, ref: Date): number | null {
  const expDate = parseExpiryDay(expRaw);
  if (!expDate) return null;
  const t0 = new Date(ref);
  t0.setHours(0, 0, 0, 0);
  expDate.setHours(0, 0, 0, 0);
  return Math.max(0, Math.ceil((expDate.getTime() - t0.getTime()) / 86400000));
}

/**
 * Keep only contracts for the expiration whose DTE is nearest to `targetDte` (same calendar convention as First Glance tabs).
 */
export function filterScannerRowsToNearestDte(rows: ScannerData[], targetDte: number, refDate: Date = new Date()): ScannerData[] {
  if (!rows?.length || !Number.isFinite(targetDte)) return rows;
  const byExp = new Map<string, ScannerData[]>();
  for (const r of rows) {
    const exp = String(r['Expiry Date'] || (r as { expiry?: string }).expiry || '').trim();
    if (!exp) continue;
    if (!byExp.has(exp)) byExp.set(exp, []);
    byExp.get(exp)!.push(r);
  }
  if (byExp.size <= 1) return rows;

  const scored: { exp: string; list: ScannerData[]; dte: number; diff: number }[] = [];
  for (const [exp, list] of Array.from(byExp.entries())) {
    const dte = calendarDteForExpiry(exp, refDate);
    if (dte == null) continue;
    scored.push({ exp, list, dte, diff: Math.abs(dte - targetDte) });
  }
  if (!scored.length) {
    return rows;
  }
  scored.sort((a, b) => a.diff - b.diff || a.dte - b.dte);
  return scored[0].list;
}

/** Keep rows whose expiry normalizes to the same YYYY-MM-DD as `expiryIso` (empty if none match). */
export function filterScannerRowsToExpiryIso(rows: ScannerData[], expiryIso: string): ScannerData[] {
  const target = String(expiryIso || '').trim();
  if (!target || !rows?.length) return [];
  return rows.filter((r) => {
    const raw = String(r['Expiry Date'] || (r as { expiry?: string }).expiry || '').trim();
    return normalizeScannerExpiryToIso(raw) === target;
  });
}

export type SpotGammaStructure = 'magnetic' | 'repulsive' | 'neutral';

export type SpotGammaPeakInsight = {
  peakStrike: number;
  peakSpotGamma: number;
  peakGammaExposure: number;
  structure: SpotGammaStructure;
  /** Geometry: where the peak sits vs spot. */
  towardPeakDirection: 'up' | 'down' | 'at';
};

/**
 * Strike with max |net spot gamma| for the underlying; uses aggregated GEX at that strike for structure.
 */
export function computeSpotGammaPeakInsight(
  rows: ScannerData[],
  underlyingSymbol: string,
  spot: number,
  dataMode: DataMode,
  targetDte?: number | null,
  expiryIso?: string | null
): SpotGammaPeakInsight | null {
  const sym = (underlyingSymbol || '').trim();
  if (!rows?.length || !sym || sym === '—' || sym === '-' || !Number.isFinite(spot) || spot <= 0) {
    return null;
  }

  const iso = (expiryIso || '').trim();
  let slice = rows;
  if (iso) {
    slice = filterScannerRowsToExpiryIso(rows, iso);
    if (!slice.length) return null;
  } else if (targetDte != null && Number.isFinite(targetDte)) {
    slice = filterScannerRowsToNearestDte(rows, Math.max(0, Math.round(targetDte)));
  }
  if (!slice.length) return null;

  const gammaRows = scannerDataToGammaStrikeData(slice as unknown as Record<string, unknown>[], sym, dataMode);
  if (!gammaRows.length) return null;

  const maxAbsGex = gammaRows.reduce((m, r) => Math.max(m, Math.abs(r.gammaExposure)), 0);

  let peak = gammaRows[0];
  let bestAbs = Math.abs(peak.spotGamma);
  for (let i = 1; i < gammaRows.length; i++) {
    const r = gammaRows[i];
    const a = Math.abs(r.spotGamma);
    if (a > bestAbs) {
      bestAbs = a;
      peak = r;
    }
  }

  if (!Number.isFinite(bestAbs) || bestAbs < 1e-18) return null;

  const eps = Math.max(spot * 1e-5, 0.02);
  let towardPeakDirection: 'up' | 'down' | 'at' = 'at';
  if (peak.strike > spot + eps) towardPeakDirection = 'up';
  else if (peak.strike < spot - eps) towardPeakDirection = 'down';

  return {
    peakStrike: peak.strike,
    peakSpotGamma: peak.spotGamma,
    peakGammaExposure: peak.gammaExposure,
    structure: classifyGexStructure(peak.gammaExposure, maxAbsGex),
    towardPeakDirection,
  };
}
